import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import React from "react";
import {
  Grid,
  CssBaseline,
  Typography,
  TextField,
  FormControlLabel,
  Paper,
  Checkbox,
  Button,
  Divider,
  Chip,
  InputAdornment,
  IconButton,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import "./UserLoginForm.less";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material";
import ColumnContainer from "../../common/ColumnContainer";
import BackgroundContainerComponent from "../../common/BackgroundImageContainer";
import redCar from "../../../assets/red_sport_car.jpeg";
import blueCar from "../../../assets/blue_sport_car.jpeg";
import lillaCar from "../../../assets/lillac_sport_car.jpeg";
import GlassContainerComponent from "../../common/GlassContainer";
import RowContainer from "../../common/RowContainer";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import useWindowDimensions from "../../../utils/useMediaQuery";
import { useLoginUserMutation } from "../../../redux/features/auth/authApi";
import ModalComponent from "../../common/ModalComponent";
// import RequestAccessModal from "./RegistrationPage";
import ThankYou from "./RegistrationThankYou";
import {
  adminDashboardRoute,
  commonRoutes,
  userDashboardRoute,
} from "../../../utils/routes/Routes";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  getLoginData,
  selectAuthData,
} from "../../../redux/features/auth/authSlice";
import { useAuth } from "../../../utils/hooks/useAuth";
import { useTokenVerify } from "../../../utils/hooks/useTokenVerify";
import { forgotPassword } from "../../../utils/hooks/useForgotPassword";
import InfoModalComponent from "../../dashboardMainLayout/InfoModalComponent";
import { ForgotPassword } from "./ForgotPassword";
import { selectSettings } from "../../../redux/features/settings/settingsSlice";
import { SkeletonLoading } from "../../common/SkeletonLoading";

export const UserLoginForm = React.memo(({ userSettings }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const decriptedTokenInfo = useTokenVerify();
  const { width } = useWindowDimensions();
  const isTablet = width < 900 ? true : false;
  const auth = useAuth();

  const backgroundImages = {
    1: redCar,
    2: lillaCar,
    3: blueCar,
  };

  const designId = userSettings?.design_id;
  const [userData, setUserData] = useState({
    username: "",
    password: "",
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [apiStateInfo, setApiStateInfo] = useState(null);
  const [openForgotPasswordModal, setOpenForgotPasswordModal] = useState(false);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleOpenRegisterPage = () => {
    navigate(commonRoutes.register);
  };
  const handleModalClose = () => setOpenModal(false);

  const [login, { data: loginData, isLoading, isSuccess, isError, error }] =
    useLoginUserMutation();

  const handleSubmitLoginForm = async (values) => {
    const formData = new URLSearchParams();
    formData.append("username", values.username);
    formData.append("password", values.password);
    formData.append("remember_me", values.rememberMe);

    try {
      const response = await login({
        user_id: userSettings?.user_id,
        userData: formData,
      });
      if (response?.data?.access_token) {
        dispatch(
          getLoginData({
            ...auth,
            token: response.data.access_token,
            is_first_access: response.data.is_first_access,
          })
        );
      } else {
        console.log("Login failed:", response);
      }
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string()
      // .email("Email non valida")
      .required("Campo obbligatorio"),
    password: Yup.string().required("Campo obbligatorio"),
  });

  const formik = useFormik({
    initialValues: userData,
    validationSchema: validationSchema,
    onSubmit: handleSubmitLoginForm,
  });

  const handleOpenForgotPasswordModal = () => {
    setOpenForgotPasswordModal(true);
  };

  const handleCloseForgotPasswordModal = () => {
    setOpenForgotPasswordModal(false);
  };

  useEffect(() => {
    const role = decriptedTokenInfo?.role;
    if (isSuccess && !isLoading && !isError && loginData) {
      if (loginData?.is_first_access) {
        navigate(commonRoutes.firstLogin);
      } else if (role === "user") {
        navigate(userDashboardRoute.dashboardHome);
      } else if (role === "admin") {
        navigate(adminDashboardRoute.dashboardHome);
      }
    }
  }, [isSuccess, navigate, loginData, isError, isLoading, decriptedTokenInfo]);

  return !designId ? (
    <Skeleton variant="rounded" width="100vw" height="100vh"></Skeleton>
  ) : (
    <BackgroundContainerComponent
      className="login-container"
      designId={userSettings?.design_id}
      backgroundimage={backgroundImages[designId]}
    >
      <CssBaseline />
      <Grid container component="main">
        <RowContainer className={`main-container design-${designId}`}>
          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            component={Paper}
            square
            maxWidth={"50vw"}
            flexBasis={"40%"}
          >
            <ColumnContainer
              alignItems="center"
              center
              fullHeight
              halfGap
              style={{ paddingLeft: isTablet ? "2rem" : "3rem" }}
            >
              <ColumnContainer halfGap style={{ maxWidth: "80%" }}>
                <RowContainer start>
                  <Typography
                    component="h2"
                    variant="h6"
                    style={{
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                      textTransform: "uppercase",
                    }}
                  >
                    <FormattedMessage id="login.title"></FormattedMessage>
                  </Typography>
                </RowContainer>
                <RowContainer start>
                  <Typography
                    component="h2"
                    variant="h4"
                    style={{
                      color: theme.palette.common.white,
                      fontWeight: 700,
                      width: "100%",
                    }}
                  >
                    <FormattedMessage id="login.subtitle"></FormattedMessage>
                  </Typography>
                </RowContainer>
              </ColumnContainer>
              <ColumnContainer halfGap>
                <RowContainer start>
                  <Typography
                    paragraph
                    style={{
                      color: theme.palette.common.white,
                      width: isTablet ? "95%" : "70%",
                    }}
                  >
                    <FormattedMessage id="login.paragraph"></FormattedMessage>
                  </Typography>
                </RowContainer>
                <Link to={commonRoutes.contacts}>
                  <Button
                    fullWidth={false}
                    variant="contained"
                    style={{
                      backgroundColor: theme.palette.common.white,
                      minWidth: isTablet ? "25%" : "45%",
                      maxWidth: isTablet ? "35%" : "50%",
                    }}
                  >
                    <span className="black-span">
                      {" "}
                      <FormattedMessage id="login.button.contact"></FormattedMessage>
                    </span>
                  </Button>
                </Link>
              </ColumnContainer>
            </ColumnContainer>
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            component={Paper}
            elevation={6}
            square
            maxWidth={"50vw"}
            flexBasis={"60%"}
          >
            <GlassContainerComponent designId={designId} padding="2% 10%">
              <ColumnContainer
                style={{
                  width: "100%",
                  gap: "10px",
                  paddingTop: "40px",
                  paddingBottom: "20px",
                }}
              >
                <ColumnContainer center minGap>
                  <RowContainer center>
                    <Typography
                      component="h1"
                      variant="h4"
                      style={{
                        color: theme.palette.primary.main,
                        textTransform: "uppercase",
                        fontWeight: 700,
                      }}
                    >
                      <FormattedMessage id={"login.form.title"} />
                    </Typography>
                  </RowContainer>
                  {isError ? (
                    <Typography
                      paragraph
                      className="error-message"
                      style={{
                        color: theme.palette.error.main,
                      }}
                    >
                      <FormattedMessage id="login.form.warnings.field"></FormattedMessage>
                    </Typography>
                  ) : null}
                </ColumnContainer>

                <form onSubmit={formik.handleSubmit}>
                  <ColumnContainer padding={"0px"} minGap>
                    <TextField
                      validateOnChange={false}
                      // validateOnBlur={false}
                      autoComplete="off"
                      variant={designId === 3 ? "standard" : "outlined"}
                      margin="normal"
                      fullWidth
                      size="small"
                      id="username"
                      label={
                        <FormattedMessage id="login.form.username"></FormattedMessage>
                      }
                      name="username"
                      value={formik.values.username}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.username &&
                        Boolean(formik.errors.username)
                      }
                      helperText={
                        formik.touched.username && formik.errors.username
                      }
                      sx={{
                        "& .MuiInputBase-root": {
                          backgroundColor:
                            designId === 3
                              ? "transparent"
                              : theme.palette.secondary.main,
                        },
                      }}
                    />
                    <TextField
                      autoComplete="off"
                      variant={designId === 3 ? "standard" : "outlined"}
                      margin="normal"
                      fullWidth
                      size="small"
                      name="password"
                      label={
                        <FormattedMessage id="login.form.password"></FormattedMessage>
                      }
                      type={showPassword ? "text" : "password"}
                      id="password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.password &&
                        Boolean(formik.errors.password)
                      }
                      helperText={
                        formik.touched.password && formik.errors.password
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleClickShowPassword}
                            >
                              {showPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          backgroundColor:
                            designId === 3
                              ? "transparent"
                              : theme.palette.secondary.main,
                        },
                      }}
                    />
                    <FormControlLabel
                      sx={{ marginTop: "12px" }}
                      control={
                        <Checkbox
                          color="primary"
                          id="rememberMe"
                          value={formik.values.rememberMe}
                          onChange={formik.handleChange}
                        />
                      }
                      label={
                        <FormattedMessage id="login.form.rememberMe"></FormattedMessage>
                      }
                    />
                    <ColumnContainer halfGap topped padding="0px">
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                      >
                        <span>
                          <FormattedMessage id="login.form.submit"></FormattedMessage>
                        </span>
                      </Button>
                    </ColumnContainer>
                  </ColumnContainer>
                </form>
                <ColumnContainer>
                  <RowContainer center>
                    <Typography
                      paragraph
                      className="link-style"
                      onClick={handleOpenForgotPasswordModal}
                    >
                      <FormattedMessage id="login.form.forgotPassword"></FormattedMessage>
                    </Typography>
                  </RowContainer>

                  <Divider
                    sx={{
                      "&::before, &::after": { borderColor: "#f5f5f5" },
                    }}
                  >
                    <Chip
                      label={
                        <FormattedMessage id="login.form.or"></FormattedMessage>
                      }
                      size="small"
                    />
                  </Divider>

                  <RowContainer center>
                    <Typography
                      paragraph
                      onClick={handleOpenRegisterPage}
                      className="link-style"
                    >
                      <FormattedMessage id="login.form.requestAccess"></FormattedMessage>
                    </Typography>
                  </RowContainer>
                </ColumnContainer>
              </ColumnContainer>
            </GlassContainerComponent>
          </Grid>
        </RowContainer>
        <ForgotPassword
          openForgotPasswordModal={openForgotPasswordModal}
          handleCloseForgotPasswordModal={handleCloseForgotPasswordModal}
        />
      </Grid>
    </BackgroundContainerComponent>
  );
});
